// Components
import { Section } from '@tracklib/kit'
import ErrorBoundary from 'errorboundary'

// Hooks
import { useIsAuthenticated } from 'modules/auth/hooks'

// Utils
import formatHtml from 'components/staticpages/formathtml'

// Types
import { CmsBlock } from 'types'

/* Blocks */
import ABTestBlock, { ABTestBlockProps } from 'components/staticpages/blocks/abtestblock'
import ArticleCategoriesBlock from 'components/staticpages/blocks/articlecategoriesblock'
import ArticleQuoteBlock from 'components/staticpages/blocks/articlequoteblock'
import ArticlesBlock from 'components/staticpages/blocks/articlesblock'
import ButtonBlock from 'components/staticpages/blocks/buttonblock'
import BeatBattle, { BeatBattleProps } from 'components/staticpages/blocks/frontpage/beatbattle'
import CenteredDescriptionBlock from 'components/staticpages/blocks/centereddescriptionblock'
import CenteredTextBlock from 'components/staticpages/blocks/centeredtextblock'
import CollectionBlock from 'components/staticpages/blocks/collectionblock'
import CollectionsBlock from 'components/staticpages/blocks/collectionsblock'
import ColumnsBlock from 'components/staticpages/blocks/columnsblock'
import ContactBlock from 'components/staticpages/blocks/contactblock'
import FaqBlock from 'components/staticpages/blocks/faqblock'
import EntitiesBlock from 'components/staticpages/blocks/entitiesblock'
import FeaturedButtonBlock from 'components/staticpages/blocks/featuredbuttonblock'
import FeaturedArticlesBlock from 'components/staticpages/blocks/featuredarticlesblock'
import FeaturedItemsBlock from 'components/staticpages/blocks/featureditemsblock'
import FrontpageHeroBlock from 'components/staticpages/blocks/frontpageheroblock'
import HeaderHeroBlock from 'components/staticpages/blocks/headerheroblock'
import HeaderBlock from 'components/staticpages/blocks/headerblock'
import HeadingBlock from 'components/staticpages/blocks/headingblock'
import Hero2021 from 'components/staticpages/blocks/frontpage/hero2021'
import HeroArticleBlock from 'components/staticpages/blocks/heroarticleblock'
import HoverPictureCollection from 'components/staticpages/blocks/hoverpicturecollection'
import Image from 'components/staticpages/blocks/imageblock'
import ImportantBlock from 'components/staticpages/blocks/importantblock'
import InfoBlock from 'components/staticpages/blocks/infoblock'
import InstagramEmbed from 'components/staticpages/blocks/instagramembed'
import LinkImageRowBlock from 'components/staticpages/blocks/frontpage/linkimagerowblock'
import MapBlock from 'components/staticpages/blocks/mapblock'
import MediaAndTextBlock from 'components/staticpages/blocks/frontpage/mediaandtextblock'
import MediaAndText2021 from 'components/staticpages/blocks/frontpage/mediaandtext2021'
import PageHeadingBlock from 'components/staticpages/blocks/pageheadingblock'
import PartnerLinksBlock from 'components/staticpages/blocks/partnerlinksblock'
import PrizesBlock from 'components/staticpages/blocks/prizesblock'
import OffersBlock from 'components/staticpages/blocks/offersblock'
import QuotesBlock from 'components/staticpages/blocks/quotesblock'
import Quotes2021 from 'components/staticpages/blocks/frontpage/quotes2021'
import QuotesGridBlock from 'components/staticpages/blocks/quotesgridblock'
import QuotesCarouselBlock from 'components/staticpages/blocks/frontpage/quotescarouselblock'
import TrackListsBlock from 'components/staticpages/blocks/tracklistsblock'
import TrackBlock from 'components/staticpages/blocks/trackblock'
import TracksCarousel from 'components/staticpages/blocks/frontpage/trackscarousel'
import VideoBlock from 'components/staticpages/blocks/videoblock'
import VideosBlock from 'components/staticpages/blocks/videosblock'
import HeroInviteFriendsBlock from 'components/staticpages/blocks/heroinvitefriendsblock'
import ShareInviteFriendsBlock from 'components/staticpages/blocks/shareinvitefriendsblock'
import SwipeCarousel from 'components/staticpages/blocks/frontpage/swipecarousel'
import SignupWithPlans from 'components/staticpages/blocks/frontpage/signupwithplans'
import Separator from 'components/staticpages/blocks/frontpage/separator'

export const RawHtmlBlock = ({ value }: { value: string }) => <span dangerouslySetInnerHTML={{ __html: value }} /> // eslint-disable-line

type StreamFieldBlockProps = {
  type: string
  value: string
  isColumn?: boolean
  isArticle?: boolean
}

export const StreamFieldBlock = (props: StreamFieldBlockProps): JSX.Element => {
  const { type, value, isArticle } = props
  switch (type) {
    case 'paragraph': {
      const html = formatHtml(value)
      if (isArticle) {
        return (
          <Section size="small">
            <p dangerouslySetInnerHTML={{ __html: html }} />
          </Section>
        )
      }
      return <p dangerouslySetInnerHTML={{ __html: html }} />
    }
    case 'ab_test':
      return <ABTestBlock {...(props as unknown as ABTestBlockProps)} />
    case 'article_categories':
      return <ArticleCategoriesBlock {...props} />
    case 'article_quote':
      return <ArticleQuoteBlock {...props} />
    case 'articles':
      return <ArticlesBlock {...props} />
    case 'button':
      return <ButtonBlock {...props} />
    case 'beat_battle':
      return <BeatBattle {...(props as unknown as BeatBattleProps)} />
    case 'centered_description':
      return <CenteredDescriptionBlock {...props} />
    case 'centered_text_block':
      return <CenteredTextBlock {...props} />
    case 'collection':
      return <CollectionBlock {...props} />
    case 'collections':
      return <CollectionsBlock {...props} />
    case 'columns':
      return <ColumnsBlock {...props} />
    case 'contact_block':
      return <ContactBlock {...props} />
    case 'entities_block':
    case 'dynamic_entities_block':
      return <EntitiesBlock {...props} />
    case 'faq':
      return <FaqBlock {...props} />
    case 'featured_blog_grid':
      return <FeaturedArticlesBlock {...props} />
    case 'featured_items':
      return <FeaturedItemsBlock {...props} />
    case 'featured_button':
      return <FeaturedButtonBlock {...props} />
    case 'front_page_hero_block':
      return <FrontpageHeroBlock {...props} />
    case 'header_hero_block':
      return <HeaderHeroBlock {...props} />
    case 'heading':
      return <HeadingBlock {...props} />
    case 'header':
      return <HeaderBlock {...props} />
    case 'hero_article':
      return <HeroArticleBlock {...props} />
    case 'hero_2021':
      return <Hero2021 {...props} />
    case 'hero_invite_friends':
      return <HeroInviteFriendsBlock {...props} />
    case 'hover_picture_collection':
      return <HoverPictureCollection {...props} />
    case 'important':
      return <ImportantBlock {...props} />
    case 'image':
      return <Image {...props} />
    case 'instagram':
      return <InstagramEmbed {...props} />
    case 'info':
      return <InfoBlock {...props} />
    case 'link_image_row':
      return <LinkImageRowBlock {...props} />
    case 'map_block':
      return <MapBlock {...props} />
    case 'media_and_text_block':
      return <MediaAndTextBlock {...props} />
    case 'media_and_text_2021':
      return <MediaAndText2021 {...props} />
    case 'partner_links':
      return <PartnerLinksBlock {...props} />
    case 'page_heading':
      return <PageHeadingBlock {...props} />
    case 'prizes':
      return <PrizesBlock {...props} />
    case 'plans_block':
      return <OffersBlock {...props} />
    case 'quotes_block':
      return <QuotesBlock {...props} />
    case 'quotes_grid_block':
      return <QuotesGridBlock {...props} />
    case 'quotes_carousel_block':
      return <QuotesCarouselBlock {...props} />
    case 'quotes_2021':
      return <Quotes2021 {...props} />
    case 'raw_html':
      return <RawHtmlBlock {...props} />
    case 'share_invite_friends':
      return <ShareInviteFriendsBlock {...props} />
    case 'song_lists':
      return <TrackListsBlock {...props} />
    case 'swipe_carousel':
      return <SwipeCarousel {...props} />
    case 'signup_with_plans':
      return <SignupWithPlans {...props} />
    case 'separator':
      return <Separator {...props} />
    case 'track':
      return <TrackBlock {...props} />
    case 'tracks_carousel':
      return <TracksCarousel {...props} />
    case 'video':
      return <VideoBlock {...props} />
    case 'videos':
      return <VideosBlock {...props} />
    default:
      return <p>Unsupported type: {type}</p>
  }
}

type StreamFieldProps = {
  blocks: Array<CmsBlock>
  isColumn?: boolean
  isArticle?: boolean
}

export const StreamField = ({ blocks, isColumn = false, isArticle = false }: StreamFieldProps): JSX.Element => {
  const isAuthenticated = useIsAuthenticated()
  return (
    <>
      {blocks
        ?.filter(block => {
          const loginRequired = block?.value?.loggedin_required
          if (isAuthenticated) {
            return loginRequired !== 2
          }
          return loginRequired !== 1
        })
        .map(({ type, value }, i) => (
          <ErrorBoundary key={String(i)}>
            <StreamFieldBlock type={type} value={value} isColumn={isColumn} isArticle={isArticle} />
          </ErrorBoundary>
        ))}
    </>
  )
}
